// noinspection JSUnresolvedReference

(function(app) {
	'use strict';

	// Configuration Module
	const CanvasConfig = {
		elements: {
			canvasContainer: null,
		},
		styles: {
			color: '',
			font: '',
			justification: '',
			mirrored: false,
			italic: false,
			bold: false,
			text: '',
			debug: false,
		},
		flag: {
			position: 'left',
			layer: null,
			spacing: 10,
			isEnabled: false
		},
		checkerboard: {
			enabled: true,
			tileSize: 20,
			colors: {
                light: '#f0f0f0',
                dark: '#e0e0e0'
			}
		},
		advancedMeasurements: false,
		defaults: {
			width: 500,
			height: 500,
            referenceWidth: 500,
			referenceHeight: 500,
			boxMargin: 0.1 // 10% margin
		}
	};

	// Canvas State Module with improved caching
	const CanvasState = {
		stage: null,
		layerBackground: null,
		layerBase: null,
		layerText: null,
		layerDebug: null,
		textFlagGroup: null,
		textNode: null,
		layerImages: [],
		colorMap: {},
		textBoundingBox: {},
		builderType: '',
		debouncedDraw: null,
		isInitialized: false,
		error: null,
		// New caching properties
		offscreenCanvas: null,
		offscreenCtx: null,
		cachedElements: new Map(),
		dirtyRegions: new Set()
	};

	// Error Handler Module
	const ErrorHandler = {
		errors: {
			CANVAS_NOT_FOUND: 'Missing required canvas element',
			STAGE_NOT_INITIALIZED: 'Stage not initialized',
			LAYER_NOT_READY: 'Layer not ready',
			IMAGE_LOAD_ERROR: 'Error loading layer images'
		},

		handleError(error, context = '') {
			CanvasState.error = error;
			console.error(`${context}: ${error}`);
			return null;
		},

		clearError() {
			CanvasState.error = null;
		}
	};

	// Performance Optimizer Module
	const PerformanceOptimizer = {
		batchDrawQueued: false,
		drawTimeout: null,
		batchOperations: new Map(),

		queueBatchDraw(layer) {
			if (this.batchDrawQueued) return;
			
			this.batchDrawQueued = true;
			if (this.drawTimeout) {
				clearTimeout(this.drawTimeout);
			}

			this.drawTimeout = setTimeout(() => {
				this.executeBatchDraw(layer);
				this.batchDrawQueued = false;
			}, 0);
		},

		executeBatchDraw(layer) {
			if (!layer || !layer.batchDraw) return;

			// Execute all batched operations
			const ctx = layer.getContext();
			ctx.save();
			
			this.batchOperations.forEach((operations, type) => {
				ctx.beginPath();
				operations.forEach(op => op(ctx));
				if (type === 'fill') ctx.fill();
				if (type === 'stroke') ctx.stroke();
			});
			
			ctx.restore();
			this.batchOperations.clear();
			
			layer.batchDraw();
		},

		optimizeImage(konvaImage) {
			if (!konvaImage) return;
			
			konvaImage.perfectDrawEnabled(false);
			konvaImage.transformsEnabled('position');
			konvaImage.listening(false);
			
			// Cache static images
			if (!konvaImage.isDynamic) {
				konvaImage.cache();
			}
			
			return konvaImage;
		}
	};

	// Utility Module
	const CanvasUtils = {
		parseColor(color) {
			if (color.startsWith('#')) {
				const r = parseInt(color.slice(1, 3), 16);
				const g = parseInt(color.slice(3, 5), 16);
				const b = parseInt(color.slice(5, 7), 16);
				return { r, g, b };
			}

			if (color.startsWith('rgb')) {
				const values = color.match(/\d+/g)?.map(Number) || [];
				return {
					r: values[0] || 0,
					g: values[1] || 0,
					b: values[2] || 0
				};
			}

			return { r: 0, g: 0, b: 0 };
		},

		getStyleString() {
			let style = '';
			if (CanvasConfig.styles.bold) style += 'bold ';
			if (CanvasConfig.styles.italic) style += 'italic ';
			return style.trim() || 'normal';
		},

getStageDimensions(canvasEl = null) {

			const wrapper = canvasEl?.closest('.wdpp-image-wrapper') || CanvasConfig.elements.canvasContainer?.closest('.wdpp-image-wrapper');

			const wrapperRect = wrapper ? wrapper.getBoundingClientRect() : null;

			const canvasRect = canvasEl ? canvasEl.getBoundingClientRect() : null;

			const width = Math.round(

				wrapperRect?.width ||

				canvasRect?.width ||

				CanvasConfig.elements.canvasContainer?.clientWidth ||

				CanvasConfig.defaults.width

			);

			const height = width;



			return { width, height };

		},

		getResponsiveTextBox() {

			const stageWidth = CanvasState.stage ? CanvasState.stage.width() : CanvasConfig.defaults.width;
			const stageHeight = CanvasState.stage ? CanvasState.stage.height() : CanvasConfig.defaults.height;
			const raw = CanvasState.textBoundingBox || {};

			const hasExplicitBox =
				raw.x !== undefined ||
				raw.y !== undefined ||
				raw.width !== undefined ||
				raw.height !== undefined;

			if (!hasExplicitBox) {
				return {};
			}

			const referenceWidth = parseFloat(raw.referenceWidth) || CanvasConfig.defaults.referenceWidth;
			const referenceHeight = parseFloat(raw.referenceHeight) || CanvasConfig.defaults.referenceHeight;
			const scaleX = stageWidth / referenceWidth;
			const scaleY = stageHeight / referenceHeight;

			return {
				x: (parseFloat(raw.x) || 0) * scaleX,
				y: (parseFloat(raw.y) || 0) * scaleY,
				width: (parseFloat(raw.width) || 0) * scaleX,
				height: (parseFloat(raw.height) || 0) * scaleY
			};
		},

		measureTextPixelSize(text, fontSize, fontFamily, fontStyle = 'normal') {
			if (!text || typeof text !== 'string' || text.trim() === '') {
				return { width: 0, height: 0, actualOffsetY: 0 };
			}

			// Check if text has line breaks
			if (text.includes('\n')) {
				return this.measureMultilineTextPixelSize(text, fontSize, fontFamily, fontStyle);
			}

			// Single line measurement (existing logic)
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');

			const estimatedWidth = fontSize * text.length;
			const estimatedHeight = fontSize * 2;

			canvas.width = Math.max(estimatedWidth, 100);
			canvas.height = Math.max(estimatedHeight, 50);

			ctx.font = `${fontStyle} ${fontSize}px ${fontFamily}`;
			ctx.textBaseline = 'top';
			

			const drawX = 10;
			const drawY = 10;
			ctx.fillText(text, drawX, drawY);

			const textMeasurements = [{
				x: drawX,
				y: drawY,
				measureText: ctx.measureText(text)
			}];

			const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

			let minX = canvas.width, maxX = -1;
			let minY = canvas.height, maxY = -1;

			for (let y = 0; y < canvas.height; y++) {
				for (let x = 0; x < canvas.width; x++) {
					const alphaIndex = (y * canvas.width + x) * 4 + 3;
					if (imageData[alphaIndex] > 0) {
						minX = Math.min(minX, x);
						maxX = Math.max(maxX, x);
						minY = Math.min(minY, y);
						maxY = Math.max(maxY, y);
					}
				}
			}

			if (maxX < minX || maxY < minY) {
				return { width: 0, height: 0, actualOffsetY: 0 };
			}

			return {
				width: maxX - minX + 1,
				height: maxY - minY + 1,
				actualOffsetY: minY - drawY,
				measurements: textMeasurements
			};
		},

		measureMultilineTextPixelSize(text, fontSize, fontFamily, fontStyle = 'normal') {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');

			const lines = text.split('\n');
			const lineHeight = fontSize * 1.2; // Same as Konva's default

			// Estimate canvas size needed
			const estimatedWidth = fontSize * Math.max(...lines.map(line => line.length));
			const estimatedHeight = lineHeight * lines.length + fontSize;

			canvas.width = Math.max(estimatedWidth, 200);
			canvas.height = Math.max(estimatedHeight, 100);

			ctx.font = `${fontStyle} ${fontSize}px ${fontFamily}`;
			ctx.textBaseline = 'top';
			
			let textMeasurements = [];

			const drawX = 10;
			let currentY = 10;

			// Draw each line
			lines.forEach(line => {
				if (line.trim() !== '') { // Only draw non-empty lines
					ctx.fillText(line, drawX, currentY);
					textMeasurements.push({
						x: drawX,
						y: currentY,
						measureText: ctx.measureText(line)
					});
				}
				currentY += lineHeight;
			});

			// Find the actual pixel bounds of all text
			const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

			let minX = canvas.width, maxX = -1;
			let minY = canvas.height, maxY = -1;

			for (let y = 0; y < canvas.height; y++) {
				for (let x = 0; x < canvas.width; x++) {
					const alphaIndex = (y * canvas.width + x) * 4 + 3;
					if (imageData[alphaIndex] > 0) {
						minX = Math.min(minX, x);
						maxX = Math.max(maxX, x);
						minY = Math.min(minY, y);
						maxY = Math.max(maxY, y);
					}
				}
			}

			if (maxX < minX || maxY < minY) {
				return { width: 0, height: 0, actualOffsetY: 0 };
			}

			return {
				width: maxX - minX + 1,
				height: maxY - minY + 1,
				actualOffsetY: minY - 10,
				measurements: textMeasurements
			};
		}
	};

	// Layer Manager Module with improved caching
	const LayerManager = {
		async loadLayerImages() {
			const layers = app.Store.getState('layers') || [];

			try {
				// Initialize offscreen canvas if needed
				if (!CanvasState.offscreenCanvas) {
					CanvasState.offscreenCanvas = new OffscreenCanvas(
						CanvasConfig.defaults.width,
						CanvasConfig.defaults.height
					);
					CanvasState.offscreenCtx = CanvasState.offscreenCanvas.getContext('2d');
				}

				CanvasState.layerImages = await Promise.all(layers.map(layer => {
					return new Promise((resolve, reject) => {
						const img = new Image();
						img.crossOrigin = 'anonymous';
						img.onload = () => {
							// Pre-render to offscreen canvas
							const cachedCanvas = new OffscreenCanvas(img.width, img.height);
							const cachedCtx = cachedCanvas.getContext('2d');
							cachedCtx.drawImage(img, 0, 0);
							
							resolve({
								img,
								id: layer.id,
								cachedCanvas
							});
						};
						img.onerror = reject;
						img.src = layer.url;
					});
				}));
			} catch (error) {
				console.error('Error loading layer images:', error);
			}
		},

		updateLayers() {
			CanvasState.layerBase.destroyChildren();

			if (CanvasConfig.flag.isEnabled && CanvasState.builderType === 'flag') {
				this.renderLayersWithFlag();
			} else {
				this.renderNormalLayers();
			}

			CanvasState.layerBase.draw();
		},

		renderLayersWithFlag() {
			CanvasState.layerImages.forEach((layer) => {
				if (layer === CanvasConfig.flag.layer) return;

				const konvaImage = this.createKonvaImage(layer);
					layer.konvaImage = konvaImage;
				CanvasState.layerBase.add(konvaImage);
			});
		},

		renderNormalLayers() {
			CanvasState.layerImages.forEach((layer) => {
				const konvaImage = this.createKonvaImage(layer);
				layer.konvaImage = konvaImage;
				CanvasState.layerBase.add(konvaImage);
			});
		},

		createKonvaImage(layer) {
			const mirrored = CanvasConfig.styles.mirrored === true;
			const width = CanvasState.stage.width();
				const konvaImage = new Konva.Image({
					image: layer.img,
					width: width,
				height: CanvasState.stage.height(),
					listening: false,
					scaleX: mirrored ? -1 : 1,
					x: mirrored ? width : 0
				});

				if (layer.color) {
				const colorValue = CanvasState.colorMap[layer.color].hex || layer.color;
					konvaImage.cache();
					konvaImage.filters([Konva.Filters.Colorize]);
				konvaImage.colorToApply = CanvasUtils.parseColor(colorValue);
			}

			return konvaImage;
		}
	};

	// Text Renderer Module with improved measurement caching
	const TextRenderer = {
		measurementCache: new Map(),
		measurementCanvas: null,
		measurementCtx: null,
		
		measureText(text, fontDeclString) {
			if (!text || typeof text !== 'string' || text.trim() === '') {
                return {
                    widthPx: 0,
                    heightPx: 0,
                    referenceCapHeightPx: 0,
                    firstLineAscentPx: 0,
                    lastLineDescentPx: 0,
                    minLeftBearingPx: 0,
                    maxRightBearingPx: 0,
                    maxLineWidthPx: 0,
                    numberOfLines: 0,
                    actualGlyphHeightForSingleLine: 0,
                    lineHeightRatio: 1 // default for single line
                };
            }

			const ctx = this.getMeasurementContext();
			ctx.save();
			ctx.font = fontDeclString; // Use the full font declaration string directly

			const lines = text.split('\n');
            const numberOfLines = lines.length;

            const hMetrics = ctx.measureText('H');
            const referenceCapHeightPx = Math.round(hMetrics.actualBoundingBoxAscent);
            const referenceFontMetricsHeightPx = Math.round(hMetrics.actualBoundingBoxAscent + hMetrics.actualBoundingBoxDescent);

            let maxLineWidthEncountered = 0;
            let overallMinLeftBearing = 0;
            let overallMaxRightBearing = 0;
            
            const lineMetricsData = [];

            for (const line of lines) {
                if (line.trim() === '') {
                    lineMetricsData.push({
                        ascent: referenceCapHeightPx, 
                        descent: referenceFontMetricsHeightPx - referenceCapHeightPx,
                        actualGlyphHeight: referenceFontMetricsHeightPx,
                        width: 0,
                        leftBearing: 0,
                        rightBearing: 0
                    });
                    maxLineWidthEncountered = Math.max(maxLineWidthEncountered, 0);
                    continue;
                }
                const metrics = ctx.measureText(line);
                const ascent = Math.round(metrics.actualBoundingBoxAscent);
                const descent = Math.round(metrics.actualBoundingBoxDescent);
                const actualGlyphHeight = ascent + descent;
                const lineWidth = Math.round(metrics.width);
                const leftBearing = Math.round(metrics.actualBoundingBoxLeft) || 0;
                const rightBearing = Math.round(metrics.actualBoundingBoxRight) || lineWidth;

                lineMetricsData.push({
                    ascent, descent, actualGlyphHeight, 
                    width: lineWidth, leftBearing, rightBearing
                });
                maxLineWidthEncountered = Math.max(maxLineWidthEncountered, lineWidth);
                overallMinLeftBearing = Math.min(overallMinLeftBearing, leftBearing);
                overallMaxRightBearing = Math.max(overallMaxRightBearing, rightBearing);
            }
            
            let totalHeightPx;
            let firstLineAscentForPositioning;
            const lastLineDescentPx = lineMetricsData.length > 0 ? lineMetricsData[lineMetricsData.length - 1].descent : 0;
            let actualGlyphHeightForSingleLine = 0;
            let lineHeightRatio = 1.2; // Default for multi-line

            if (numberOfLines === 1) {
                const singleLine = lineMetricsData[0];
                totalHeightPx = singleLine.actualGlyphHeight;
                firstLineAscentForPositioning = singleLine.ascent;
                actualGlyphHeightForSingleLine = singleLine.actualGlyphHeight;
                lineHeightRatio = 1; // No extra line height for single lines
            } else {
                // fontDeclString is e.g., "italic bold 100px Arial"
                // We need to parse the fontSize from fontDeclString or pass it as a separate parameter.
                // For simplicity, let's assume fontDeclString always has <size>px format and parse it.
                // This is a bit fragile. A better way would be to pass fontSize separately to measureText.
                // However, to minimize interface changes for now:
                const fontSizeMatch = fontDeclString.match(/(\d+)px/);
                const measuredFontSize = fontSizeMatch ? parseInt(fontSizeMatch[1], 10) : referenceFontMetricsHeightPx;

                const baselineToBaselineSpacing = Math.round(measuredFontSize * lineHeightRatio);
                let currentY = 0;
                const firstLine = lineMetricsData[0];
                let overallTopEdge = 0 - firstLine.ascent;
                for (let i = 0; i < numberOfLines - 1; i++) {
                    currentY += baselineToBaselineSpacing;
                }
                const lastLine = lineMetricsData[numberOfLines - 1];
                let overallBottomEdge = currentY + lastLine.descent;
                totalHeightPx = Math.round(overallBottomEdge - overallTopEdge);
                firstLineAscentForPositioning = Math.round(0 - overallTopEdge);
            }
            
            const effectiveWidthPx = Math.round(overallMaxRightBearing - overallMinLeftBearing);

			ctx.restore();

			return {
				widthPx: effectiveWidthPx, 
                heightPx: totalHeightPx, 
                referenceCapHeightPx: referenceCapHeightPx,
                firstLineAscentPx: firstLineAscentForPositioning,
                lastLineDescentPx: lastLineDescentPx,
                minLeftBearingPx: overallMinLeftBearing,
                maxRightBearingPx: overallMaxRightBearing,
                maxLineWidthPx: maxLineWidthEncountered,
                numberOfLines: numberOfLines,
                actualGlyphHeightForSingleLine: actualGlyphHeightForSingleLine,
                lineHeightRatio: lineHeightRatio // Pass this to Konva
			};
		},

		getMeasurementContext() {
			if (!this.measurementCanvas) {
				this.measurementCanvas = document.createElement('canvas');
				this.measurementCtx = this.measurementCanvas.getContext('2d', {
					alpha: false,
					willReadFrequently: true
				});
			}
			return this.measurementCtx;
		},

		setupTextNode(baseFontSize, fontFamily, fontStyle, finalAlign) {
            // Construct the full font string for measurement
            const fullFontString = `${fontStyle} ${baseFontSize}px ${fontFamily}`;

			const metrics = this.measureText(
				CanvasConfig.styles.text,
                fullFontString
			);

            // Use the calculated lineHeightRatio from metrics
            const konvaLineHeight = metrics.lineHeightRatio;

			// For flag rendering, we need special positioning
			// The text baseline should align with the bottom of the flag
			const isWithFlag = CanvasConfig.flag.isEnabled && CanvasState.builderType === 'flag';
			
			let xOffset, yOffset;
			
			if (isWithFlag) {
				// When with flag: position text so baseline aligns with flag bottom
				// Measure capital H for flag height reference
				const hMetrics = this.measureH(baseFontSize, fontFamily, fontStyle);
				
				// The text node should be positioned so its baseline is at y=0 (which will be the flag bottom)
				xOffset = metrics.minLeftBearingPx * -1;
				yOffset = -6; // Baseline at y=0 for alignment with flag bottom
			} else {
				// Normal text positioning (no flag)
				xOffset = metrics.minLeftBearingPx * -1; // Compensate for negative left bearing
				yOffset = metrics.firstLineAscentPx * -1; // Compensate for ascent to align top visually
			}


			// If textNode was destroyed, create a new one
			if (!CanvasState.textNode || CanvasState.textNode.destroyed) {
				CanvasState.textNode = new Konva.Text({
					text: CanvasConfig.styles.text,
					fontSize: baseFontSize, // This is the reference font size, Konva scales from this.
					fontFamily: fontFamily,
					fontStyle: fontStyle, // bold, italic, bold italic, normal
					lineHeight: konvaLineHeight, // Konva's multiplier for line height
					align: finalAlign,
                    x: xOffset, 
                    y: yOffset,
                    fill: CanvasConfig.styles.color || '#000000',
                    padding: 0, // Explicitly set padding if any, otherwise Konva might add some
                    // verticalAlign: 'top' // Might be useful to ensure y is the top edge
				});
			} else {
				// Update existing textNode
				CanvasState.textNode.setAttrs({
					text: CanvasConfig.styles.text,
					fontSize: baseFontSize,
					fontFamily: fontFamily,
					fontStyle: fontStyle,
					lineHeight: konvaLineHeight,
					align: finalAlign,
                    x: xOffset,
                    y: yOffset,
					fill: CanvasConfig.styles.color || '#000000'
				});
			}

			CanvasState.textFlagGroup.add(CanvasState.textNode);
		},

		renderTextOnly() {
			const stageWidth = CanvasState.stage.width();
			const stageHeight = CanvasState.stage.height();

            /* gamle css afhængig responsive håntering
			const boxX = parseFloat(CanvasState.textBoundingBox.x) || stageWidth * 0.1;
			const boxY = parseFloat(CanvasState.textBoundingBox.y) || stageHeight * 0.1;
			const boxWidth = parseFloat(CanvasState.textBoundingBox.width) || stageWidth * 0.8;
			const boxHeight = parseFloat(CanvasState.textBoundingBox.height) || stageHeight * 0.8;
            */
            const responsiveTextBox = CanvasUtils.getResponsiveTextBox();
			const boxX = responsiveTextBox.x || stageWidth * 0.1;
			const boxY = responsiveTextBox.y || stageHeight * 0.1;
			const boxWidth = responsiveTextBox.width || stageWidth * 0.8;
			const boxHeight = responsiveTextBox.height || stageHeight * 0.8;

			const finalAlign = CanvasConfig.styles.justification || 'center';
			const fontFamily = CanvasConfig.styles.font || 'Arial';
			const fontStyle = CanvasUtils.getStyleString();
			const baseFontSize = 100;

			// Remove children without destroying them
			CanvasState.textFlagGroup.removeChildren();

			// Use pixel-perfect measurement for ALL text (single and multiline)
			const pixelMetrics = CanvasUtils.measureTextPixelSize(
				CanvasConfig.styles.text,
				baseFontSize,
				fontFamily,
				fontStyle
			);

			// Setup text node
			this.setupTextNode(baseFontSize, fontFamily, fontStyle, finalAlign);
			
			// Reserve space for advanced measurement overlays so tall text does not
			// scale all the way to the bottom edge and hide the width indicator.
			const widthIndicatorReserve = CanvasConfig.advancedMeasurements ? 40 : 0;
			const safeBoxHeight = Math.max(1, boxHeight - widthIndicatorReserve);

			// Calculate the scale to fit the box while maintaining aspect ratio
			const scale = Math.min(
				boxWidth / pixelMetrics.width,
				safeBoxHeight / pixelMetrics.height
			);

			// VERTICAL CENTERING: Use pixel-perfect measurements for ALL text
			const scaledTextHeight = pixelMetrics.height * scale;
			const verticalOffset = (safeBoxHeight - scaledTextHeight) / 2;

			// Position the text node using pixel-perfect offset for ALL text
			CanvasState.textNode.y(-pixelMetrics.actualOffsetY);
			CanvasState.textFlagGroup.add(CanvasState.textNode);

			// Reset group positioning first for accurate bounds calculation
			CanvasState.textFlagGroup.x(0);
			CanvasState.textFlagGroup.y(0);
			CanvasState.textFlagGroup.scaleX(1);
			CanvasState.textFlagGroup.scaleY(1);
			CanvasState.textFlagGroup.offsetX(0);
			CanvasState.textFlagGroup.offsetY(0);

			// HORIZONTAL CENTERING: Use group bounds to preserve text justification
			const groupBounds = CanvasState.textFlagGroup.getClientRect({ skipTransform: false });
			const finalGroupX = boxX + (boxWidth - (groupBounds.width * scale)) / 2;
			const finalGroupY = boxY + verticalOffset;

			// Apply final positioning and scaling
			CanvasState.textFlagGroup.setAttrs({
				x: finalGroupX,
				y: finalGroupY,
				scaleX: scale * (CanvasConfig.styles.mirrored ? -1 : 1),
				scaleY: scale,
				offsetX: CanvasConfig.styles.mirrored ? groupBounds.width : 0,
				pixelMetrics: pixelMetrics
			});

			// Adjust for group bounds offset (horizontal only to preserve pixel-perfect vertical centering)
			CanvasState.textFlagGroup.x(finalGroupX - groupBounds.x * scale * (CanvasConfig.styles.mirrored ? -1 : 1));
			// Keep the pixel-perfect vertical positioning - don't adjust for groupBounds.y
		},

		redraw() {
			const stageWidth = CanvasState.stage.width();
			const stageHeight = CanvasState.stage.height();

            /* gamle css afhængig responsive håntering
			const boxX = parseFloat(CanvasState.textBoundingBox.x) || stageWidth * 0.1;
			const boxY = parseFloat(CanvasState.textBoundingBox.y) || stageHeight * 0.1;
			const boxWidth = parseFloat(CanvasState.textBoundingBox.width) || stageWidth * 0.8;
			const boxHeight = parseFloat(CanvasState.textBoundingBox.height) || stageHeight * 0.8;
            */

            const responsiveTextBox = CanvasUtils.getResponsiveTextBox();
			const boxX = responsiveTextBox.x || stageWidth * 0.1;
			const boxY = responsiveTextBox.y || stageHeight * 0.1;
			const boxWidth = responsiveTextBox.width || stageWidth * 0.8;
			const boxHeight = responsiveTextBox.height || stageHeight * 0.8;

			const finalAlign = CanvasConfig.styles.justification || 'center';
			const fontFamily = CanvasConfig.styles.font || 'Arial';
			const fontStyle = CanvasUtils.getStyleString();
			const baseFontSize = 100;

			if (CanvasConfig.flag.isEnabled && CanvasState.builderType === 'flag') {
				// Remove children without destroying them
				CanvasState.textFlagGroup.removeChildren();

				// Setup text node with cached measurements if available
				this.setupTextNode(baseFontSize, fontFamily, fontStyle, finalAlign);
				
				// Add flag if enabled
				this.addFlagToGroup(baseFontSize, fontFamily, fontStyle);

				// Position the group
				this.positionGroup(boxX, boxY, boxWidth, boxHeight);
			} else {
				// Use new text-only rendering
				this.renderTextOnly();
			}

			// Queue batch draw
			PerformanceOptimizer.queueBatchDraw(CanvasState.layerText);
		},

		measureH(fontSize, fontFamily, fontStyle) {
			// Measure capital H to get its exact height
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			
			canvas.width = fontSize * 2;
			canvas.height = fontSize * 2;
			
			ctx.font = `${fontStyle} ${fontSize}px ${fontFamily}`;
			ctx.textBaseline = 'alphabetic';
			ctx.fillStyle = 'black';
			
			const x = fontSize / 2;
			const y = fontSize;
			
			ctx.fillText('H', x, y);
			
			const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
			
			let minY = canvas.height, maxY = -1;
			
			for (let py = 0; py < canvas.height; py++) {
				for (let px = 0; px < canvas.width; px++) {
					const idx = (py * canvas.width + px) * 4 + 3;
					if (imageData[idx] > 0) {
						minY = Math.min(minY, py);
						maxY = Math.max(maxY, py);
					}
				}
			}
			
			const hHeight = maxY - minY + 1;
			const hAscent = y - minY; // Distance from baseline to top of H
			
			return {
				height: hHeight,
				ascent: hAscent,
				baseline: y,
				top: minY
			};
		},

		addFlagToGroup(baseFontSize, fontFamily, fontStyle) {
			if (!CanvasConfig.flag.layer || !CanvasConfig.flag.layer.img) return;

			const flagImg = CanvasConfig.flag.layer.img;
			const flagAspectRatio = flagImg.width / flagImg.height;
			
			// IMPORTANT: Use capital H height for flag height, not full text height
			const hMetrics = this.measureH(baseFontSize, fontFamily, fontStyle);
			const flagHeight = hMetrics.height;
			const flagWidth = flagHeight * flagAspectRatio;

			const konvaFlagImage = this.createFlagImage(flagImg, flagWidth, flagHeight);
			this.positionFlag(konvaFlagImage, hMetrics, flagWidth);
		},

		createFlagImage(flagImg, flagWidth, flagHeight) {
				const konvaFlagImage = new Konva.Image({
					image: flagImg,
					width: flagWidth,
					height: flagHeight,
					listening: false,
				name: 'debugFlag'
				});

			if (CanvasConfig.flag.layer.color) {
				const colorValue = CanvasState.colorMap[CanvasConfig.flag.layer.color].hex || CanvasConfig.flag.layer.color;
					konvaFlagImage.cache();
					konvaFlagImage.filters([Konva.Filters.Colorize]);
				konvaFlagImage.colorToApply = CanvasUtils.parseColor(colorValue);
				}
				
			return konvaFlagImage;
		},

		positionFlag(konvaFlagImage, hMetrics, flagWidth) {
			// Position flag so its bottom aligns with the baseline (y=0)
			// Since we set offsetY to flagHeight, y=0 now represents the bottom of the flag
			// So we set y to 0 to align the flag bottom with the baseline
			konvaFlagImage.y(0); // Flag bottom at baseline
			console.log('hMetrics.height: ' + hMetrics.height);
			
			// Store flag metrics for debug lines
			CanvasConfig.flag.currentHeight = hMetrics.height;
			CanvasConfig.flag.currentY = 0; // Flag starts at baseline

			// Get the text node's actual position and dimensions
			const textNodeX = CanvasState.textNode.x();
			const textNodeWidth = CanvasState.textNode.width();
			
			if (CanvasConfig.flag.position === 'left') {
				// Position flag to the left of text with consistent spacing
				konvaFlagImage.x(textNodeX - flagWidth - CanvasConfig.flag.spacing);
			} else { // right
				// Position flag to the right of text with consistent spacing
				konvaFlagImage.x(textNodeX + textNodeWidth + CanvasConfig.flag.spacing);
			}

			CanvasState.textFlagGroup.add(konvaFlagImage);
		},

		

		positionGroup(boxX, boxY, boxWidth, boxHeight) {
			CanvasState.textFlagGroup.x(0);
			CanvasState.textFlagGroup.y(0);
			CanvasState.textFlagGroup.scaleX(1);
			CanvasState.textFlagGroup.scaleY(1);
			CanvasState.textFlagGroup.offsetX(0);
			CanvasState.textFlagGroup.offsetY(0);

			const groupBounds = CanvasState.textFlagGroup.getClientRect({ skipTransform: false });

			let scale = 1;
			if (groupBounds.width > 0 && groupBounds.height > 0) {
				const scaleXFactor = boxWidth / groupBounds.width;
				const scaleYFactor = boxHeight / groupBounds.height;
				scale = Math.min(scaleXFactor, scaleYFactor);
			}
			
			const finalGroupX = boxX + (boxWidth - (groupBounds.width * scale)) / 2;
			const finalGroupY = boxY + (boxHeight - (groupBounds.height * scale)) / 2;
			
			CanvasState.textFlagGroup.setAttrs({
				x: finalGroupX,
				y: finalGroupY,
				scaleX: scale * (CanvasConfig.styles.mirrored ? -1 : 1),
				scaleY: scale,
				offsetX: CanvasConfig.styles.mirrored ? groupBounds.width : 0,
				offsetY: 0
			});

			CanvasState.textFlagGroup.x(finalGroupX - groupBounds.x * scale * (CanvasConfig.styles.mirrored ? -1 : 1));
			CanvasState.textFlagGroup.y(finalGroupY - groupBounds.y * scale);
		},

		clearCache() {
			this.measurementCache.clear();
			if (this.measurementCanvas) {
				this.measurementCanvas = null;
				this.measurementCtx = null;
			}
		}
	};



	// Debug Renderer Module
	const DebugRenderer = {
		drawDebugShapes() {
			if (!CanvasState.layerDebug) return;

			CanvasState.layerDebug.destroyChildren();

			// Always draw baseline and top lines when flag is enabled (not just in debug mode)
			
			if (CanvasConfig.flag.isEnabled && CanvasState.builderType === 'flag') {
				setTimeout(function() {
					this.drawBaselineAndTopLines();
				}.bind(this), 300);
			}

			
			if(CanvasConfig.advancedMeasurements){
				setTimeout(function() {
					this.drawAdvancedMeasurements();
				}.bind(this), 300);
			}
			

			if (CanvasConfig.styles.debug) {
				this.drawBoundingBox();
				
				if (CanvasConfig.flag.isEnabled && CanvasState.builderType === 'flag') {
					this.drawTextAndFlagBounds();
				}
			}
			CanvasState.layerDebug.batchDraw();
		},

		hasOverAscenders(text){
			const hasOverAscenders = /[ÀÁÂÃÄÅÈÉÊËÌÍÎÏÒÓÔÕÖÙÚÛÜÝŶŸČĆĎĚŃŇŘŠŤŽ]/.test(text);
			return hasOverAscenders;
		},

		hasAscenders(text){
			const hasAscenders = /[bdfhklijåtöüÿäABCDEFGHIKLMNOPQRSTUVWXYZÆØÅ]/.test(text);
			return hasAscenders;
		},

		hasDescenders(text){
			const hasDescenders = /[gjpqyçģğĝġĵƥṗɋýÿŷỳỵȳ]/.test(text);
			return hasDescenders;
		},

		drawBaselineAndTopLines() {
			if (!CanvasState.textFlagGroup) return;
			
			// Get the absolute position of the text/flag group
			const groupTransform = CanvasState.textFlagGroup.getAbsoluteTransform();
			const groupPos = CanvasState.textFlagGroup.getAbsolutePosition();
			const groupBounds = CanvasState.textFlagGroup.getClientRect({ relativeTo: CanvasState.stage });
			// Calculate the baseline position (y=0 in the group)
			const baselinePoint = groupTransform.point({x: 0, y: 0});
			
			// Calculate the top of flag position
			// Flag starts at y=0 (baseline) and extends upward by H height
			const flagTopY = (CanvasConfig.flag.currentHeight || 0);
			const topPoint = groupTransform.point({x: 0, y: flagTopY});
			
			const stageWidth = CanvasState.stage.width();
			
			// Draw baseline (red dashed line)
			const baselineLine = new Konva.Line({
				points: [0, baselinePoint.y, stageWidth, baselinePoint.y],
				stroke: 'red',
				strokeWidth: 1,
				dash: [5, 5],
				opacity: 0.7,
				listening: false
			});
			CanvasState.layerDebug.add(baselineLine);
			
			// Draw top of flag line (blue dashed line)
			const topLine = new Konva.Line({
				points: [0, topPoint.y, stageWidth, topPoint.y],
				stroke: 'blue',
				strokeWidth: 1,
				dash: [5, 5],
				opacity: 0.7,
				listening: false
			});
			CanvasState.layerDebug.add(topLine);
			
			if(this.hasOverAscenders(CanvasConfig.styles.text)){
				//plus 4 er justering i forhold til Helvetica
				const acenderLine = new Konva.Line({
					points: [0, topPoint.y - groupBounds.height + 3, stageWidth, topPoint.y - groupBounds.height + 3],
					stroke: 'green',
					strokeWidth: 1,
					dash: [5, 5],
					opacity: 0.7,
					listening: false
				});
				CanvasState.layerDebug.add(acenderLine);
			}

			if(this.hasDescenders(CanvasConfig.styles.text)){
				const acenderLine = new Konva.Line({
					points: [0, groupBounds.y + groupBounds.height, stageWidth, groupBounds.y + groupBounds.height],
					stroke: 'orange',
					strokeWidth: 1,
					dash: [5, 5],
					opacity: 0.7,
					listening: false
				});
				CanvasState.layerDebug.add(acenderLine);
			}

			const unifiedData = app.DataManager.getUnifiedData()

			let baselineTextHeight = 0;
			let totalTextHeight = 0;

			if(unifiedData.textBaseHeight){
				baselineTextHeight = unifiedData.textBaseHeight;
			}

			if(unifiedData.textHeight){
				totalTextHeight = unifiedData.textHeight;
			}

			console.log('---...---');
			console.log(app.DataManager.getUnifiedData());
			console.log("baselineTextHeight : " + baselineTextHeight);
			console.log("totalTextHeight : " + totalTextHeight);

			if(this.hasOverAscenders(CanvasConfig.styles.text) && this.hasDescenders(CanvasConfig.styles.text)){
				this.drawHeightMeasurement(topPoint.y - groupBounds.height, groupBounds.y + groupBounds.height + 3, stageWidth - 20, totalTextHeight);
			}
			if(this.hasOverAscenders(CanvasConfig.styles.text) && !this.hasDescenders(CanvasConfig.styles.text)){
				this.drawHeightMeasurement(topPoint.y - groupBounds.height, topPoint.y + 3, stageWidth - 20, totalTextHeight);
			}
			if(!this.hasOverAscenders(CanvasConfig.styles.text) && this.hasDescenders(CanvasConfig.styles.text)){
				this.drawHeightMeasurement(baselinePoint.y, groupBounds.y + groupBounds.height, stageWidth - 20, totalTextHeight);
			}

			// Draw vertical line with arrows and height pill
			this.drawHeightMeasurement(baselinePoint.y, topPoint.y, 20, baselineTextHeight);

			// Always draw total width measurement
			this.drawTotalWidthMeasurement(groupBounds);

			// Only add labels in debug mode
			if (CanvasConfig.styles.debug) {
				const baselineLabel = new Konva.Text({
					x: 10,
					y: baselinePoint.y - 15,
					text: 'Baseline',
					fontSize: 12,
					fill: 'red',
					listening: false
				});
				CanvasState.layerDebug.add(baselineLabel);
				
				const topLabel = new Konva.Text({
					x: 10,
					y: topPoint.y - 15,
					text: 'Top of Flag (H height)',
					fontSize: 12,
					fill: 'blue',
					listening: false
				});
				CanvasState.layerDebug.add(topLabel);
			}


		},	
		
		drawAdvancedMeasurements() {


			const pixelMetrics = CanvasState.textFlagGroup.attrs.pixelMetrics;
			const scaleY = CanvasState.textFlagGroup.attrs.scaleY;
			const stageWidth = CanvasState.stage.width();
			const groupBounds = CanvasState.textFlagGroup.getClientRect({ relativeTo: CanvasState.stage });
			const unifiedData = app.DataManager.getUnifiedData();
			
			let totalTextHeight = 0;
			if(unifiedData.textHeight){
				totalTextHeight = unifiedData.textHeight;
			}

			if(pixelMetrics.measurements){

				let topAdjustment = 0;
				let bottomAdjustment = -10;
				if(pixelMetrics.measurements.length > 1){
					topAdjustment = 10;
					bottomAdjustment = 0;
				}

				const topPoint = groupBounds.y - ((pixelMetrics.measurements[0].measureText.actualBoundingBoxAscent - topAdjustment) * scaleY);
				const bottomPoint = groupBounds.y + (pixelMetrics.measurements[pixelMetrics.measurements.length - 1].measureText.actualBoundingBoxDescent * scaleY) + ((pixelMetrics.measurements[pixelMetrics.measurements.length - 1].y + bottomAdjustment) * scaleY);
				
				/*
				const topLine = new Konva.Line({
					points: [0, topPoint, stageWidth, topPoint],
					stroke: 'green',
					strokeWidth: 1,
					dash: [5, 5],
					opacity: 0.7,
					listening: false
				});
				CanvasState.layerDebug.add(topLine);

				const bottomLine = new Konva.Line({
					points: [0, bottomPoint, stageWidth, bottomPoint],
					stroke: 'green',
					strokeWidth: 1,
					dash: [5, 5],
					opacity: 0.7,
					listening: false
				});
				CanvasState.layerDebug.add(bottomLine);
				*/

				this.drawHeightMeasurement(topPoint, bottomPoint, stageWidth - 20, totalTextHeight);

				this.drawTotalWidthMeasurement(groupBounds);

			}

		},

		drawHeightMeasurement(baselineY, topY, xPosition, textHeight) {

			console.log("baselineY: " + baselineY + ", topY: " + topY + ", xPosition: " + xPosition + ", textHeight: " + textHeight);			
			// Draw vertical line
			const verticalLine = new Konva.Line({
				points: [xPosition, topY, xPosition, baselineY],
				stroke: '#333',
				strokeWidth: 1,
				opacity: 0.8,
				listening: false
			});
			CanvasState.layerDebug.add(verticalLine);
			
			const topCapLine = new Konva.Line({
				points: [xPosition - 7, topY, xPosition + 7, topY],
				stroke: '#333',
				strokeWidth: 1,
				opacity: 0.8,
				listening: false
			});
			CanvasState.layerDebug.add(topCapLine);
			
			const bottomCapLine = new Konva.Line({
				points: [xPosition - 7, baselineY, xPosition + 7, baselineY],
				stroke: '#333',
				strokeWidth: 1,
				opacity: 0.8,
				listening: false
			});
			CanvasState.layerDebug.add(bottomCapLine);

			// Draw top arrow
			/*
			const topArrow = new Konva.Arrow({
				points: [xPosition, topY + 8, xPosition, topY],
				pointerLength: 8,
				pointerWidth: 8,
				fill: '#333',
				stroke: '#333',
				strokeWidth: 1,
				opacity: 0.8,
				listening: false
			});
			CanvasState.layerDebug.add(topArrow);*/
			
			// Draw bottom arrow
			/*
			const bottomArrow = new Konva.Arrow({
				points: [xPosition, baselineY - 8, xPosition, baselineY],
				pointerLength: 8,
				pointerWidth: 8,
				fill: '#333',
				stroke: '#333',
				strokeWidth: 1,
				opacity: 0.8,
				listening: false
			});
			CanvasState.layerDebug.add(bottomArrow);*/

			if(!textHeight){
				let textHeight = 'N/A';
			} else {
				textHeight = textHeight + ' cm';
			}
						
			// Calculate pill position (middle of the vertical line)
			const pillY = (topY + baselineY) / 2;
			
			// Create the pill background
			const pillText = String(textHeight);
			const pillFontSize = 11;
			const pillPadding = 6;
			
			// Measure text for pill sizing
			const tempText = new Konva.Text({
				text: pillText,
				fontSize: pillFontSize,
				fontFamily: 'Arial'
			});
			const textWidth = tempText.width();
			const textHeight2 = tempText.height();
			
			// Draw pill background
			const pillWidth = textWidth + pillPadding * 2;
			const pillHeight = textHeight2 + pillPadding;
			const pillX = xPosition - pillWidth / 2;
			
			const pillBg = new Konva.Rect({
				x: pillX,
				y: pillY - pillHeight / 2,
				width: pillWidth,
				height: pillHeight,
				fill: 'white',
				stroke: '#333',
				strokeWidth: 1,
				cornerRadius: pillHeight / 2,
				opacity: 0.9,
				listening: false
			});
			CanvasState.layerDebug.add(pillBg);
			
			// Draw pill text
			const pillTextNode = new Konva.Text({
				x: pillX + pillPadding,
				y: pillY - textHeight2 / 2,
				text: pillText,
				fontSize: pillFontSize,
				fontFamily: 'Arial',
				fill: '#333',
				listening: false
			});
			CanvasState.layerDebug.add(pillTextNode);
		},

		drawTotalWidthMeasurement(groupBounds) {
			// Position the horizontal line below the sticker
			const yPosition = groupBounds.y + groupBounds.height + 20;
			//plus 2 er justering i forhold til Helvetica
			const leftX = groupBounds.x + 2;
			const rightX = groupBounds.x + groupBounds.width;
			
			// Draw horizontal line
			const horizontalLine = new Konva.Line({
				points: [leftX, yPosition, rightX, yPosition],
				stroke: '#333',
				strokeWidth: 1,
				opacity: 0.8,
				listening: false
			});
			CanvasState.layerDebug.add(horizontalLine);
			

			const verticalLineLeft = new Konva.Line({
				points: [leftX, yPosition - 7, leftX, yPosition + 7],
				stroke: '#333',
				strokeWidth: 1,
				opacity: 0.8,
				listening: false
			});
			CanvasState.layerDebug.add(verticalLineLeft);

			const verticalLineRight = new Konva.Line({
				points: [rightX, yPosition - 7, rightX, yPosition + 7],
				stroke: '#333',
				strokeWidth: 1,
				opacity: 0.8,
				listening: false
			});
			CanvasState.layerDebug.add(verticalLineRight);

			// Draw left arrow
			/*
			const leftArrow = new Konva.Arrow({
				points: [leftX - 8, yPosition, leftX, yPosition],
				pointerLength: 8,
				pointerWidth: 8,
				fill: '#333',
				stroke: '#333',
				strokeWidth: 1,
				opacity: 0.8,
				listening: false
			});
			CanvasState.layerDebug.add(leftArrow);
			
			// Draw right arrow
			const rightArrow = new Konva.Arrow({
				points: [rightX + 8, yPosition, rightX, yPosition],
				pointerLength: 8,
				pointerWidth: 8,
				fill: '#333',
				stroke: '#333',
				strokeWidth: 1,
				opacity: 0.8,
				listening: false
			});
			CanvasState.layerDebug.add(rightArrow);*/
			
			// Get the width value from store
			let widthText = 'N/A';
			const widthValue = app.Store.getState('text-width');
			if (widthValue) {
				let widthValueRounded = Math.round(widthValue * 10) / 10
				widthText = widthValueRounded + ' cm';
			}
			
			// Calculate pill position (center of the horizontal line)
			const pillX = (leftX + rightX) / 2;
			
			// Create the pill
			const pillText = String(widthText);
			const pillFontSize = 11;
			const pillPadding = 6;
			
			// Measure text for pill sizing
			const tempText = new Konva.Text({
				text: pillText,
				fontSize: pillFontSize,
				fontFamily: 'Arial'
			});
			const textWidth = tempText.width();
			const textHeight = tempText.height();
			
			// Draw pill background
			const pillWidth = textWidth + pillPadding * 2;
			const pillHeight = textHeight + pillPadding;
			
			const pillBg = new Konva.Rect({
				x: pillX - pillWidth / 2,
				y: yPosition - pillHeight / 2,
				width: pillWidth,
				height: pillHeight,
				fill: 'white',
				stroke: '#333',
				strokeWidth: 1,
				cornerRadius: pillHeight / 2,
				opacity: 0.9,
				listening: false
			});
			CanvasState.layerDebug.add(pillBg);
			
			// Draw pill text
			const pillTextNode = new Konva.Text({
				x: pillX - textWidth / 2,
				y: yPosition - textHeight / 2,
				text: pillText,
				fontSize: pillFontSize,
				fontFamily: 'Arial',
				fill: '#333',
				listening: false
			});
			CanvasState.layerDebug.add(pillTextNode);
		},

		drawBoundingBox() {
			const stageWidth = CanvasState.stage.width();
			const stageHeight = CanvasState.stage.height();

            /* Gamle responsive tekst
			const boxX = parseFloat(CanvasState.textBoundingBox.x) || stageWidth * 0.1;
			const boxY = parseFloat(CanvasState.textBoundingBox.y) || stageHeight * 0.1;
			const boxWidth = parseFloat(CanvasState.textBoundingBox.width) || stageWidth * 0.8;
			const boxHeight = parseFloat(CanvasState.textBoundingBox.height) || stageHeight * 0.8;
            */

            const responsiveTextBox = CanvasUtils.getResponsiveTextBox();
			const boxX = responsiveTextBox.x || stageWidth * 0.1;
			const boxY = responsiveTextBox.y || stageHeight * 0.1;
			const boxWidth = responsiveTextBox.width || stageWidth * 0.8;
			const boxHeight = responsiveTextBox.height || stageHeight * 0.8;


				const debugRect = new Konva.Rect({
					x: boxX,
					y: boxY,
					width: boxWidth,
					height: boxHeight,
					stroke: 'red',
					strokeWidth: 2,
				dash: [10, 5],
					listening: false
				});
			CanvasState.layerDebug.add(debugRect);
		},

		drawTextAndFlagBounds() {
			if (CanvasState.textNode && CanvasState.textNode.getStage()) {
				const textRectAbs = CanvasState.textNode.getClientRect({ relativeTo: CanvasState.stage });
						const debugTextRect = new Konva.Rect({
							x: textRectAbs.x,
							y: textRectAbs.y,
							width: textRectAbs.width,
							height: textRectAbs.height,
							stroke: 'green',
							strokeWidth: 1,
							listening: false
						});
				CanvasState.layerDebug.add(debugTextRect);
			}

			if (CanvasState.textFlagGroup) {
				const konvaFlagImage = CanvasState.textFlagGroup.findOne('.debugFlag');
						if (konvaFlagImage && konvaFlagImage.getStage()) {
					const flagRectAbs = konvaFlagImage.getClientRect({ relativeTo: CanvasState.stage });
							const debugFlagRect = new Konva.Rect({
								x: flagRectAbs.x,
								y: flagRectAbs.y,
								width: flagRectAbs.width,
								height: flagRectAbs.height,
								stroke: 'blue',
								strokeWidth: 2,
								dash: [5, 5],
								listening: false
							});
					CanvasState.layerDebug.add(debugFlagRect);
				}
			}
		}
	};

	// Main Canvas Renderer Module
	app.CanvasRenderer = {
		priority: -10,

		init() {
			try {
				ErrorHandler.clearError();
				CanvasState.debouncedDraw = app.Utils.debounce((text = true, layers = true) => this.doDraw(text, layers), 25);
				
				if (!this.initializeCanvas()) {
					return false;
				}

				this.setupEventListeners();
				this.createCheckerboardBackground();
				
				return LayerManager.loadLayerImages().then(() => {
					CanvasState.layerImages.sort((a, b) => parseInt(a.index) - parseInt(b.index));

					if (CanvasConfig.flag.isEnabled && CanvasState.layerImages.length > 0) {
						CanvasConfig.flag.layer = CanvasState.layerImages[0];
					}

					// Force initial layer colors to be applied if they exist
					const initialLayerColors = app.Store.getState('layerColors');
					if (initialLayerColors && initialLayerColors.length > 0) {
						this.updateLayerColors(initialLayerColors);
					}
					
					// Apply initial text color if it exists
					const initialTextColor = app.Store.getState('textColor');
					if (initialTextColor) {
						this.updateTextColor(initialTextColor);
					}

					LayerManager.updateLayers();
					CanvasState.debouncedDraw();
					
					CanvasState.isInitialized = true;
					setTimeout(() => app.Store.setState('canvasReady', true), 100);
					return true;
				}).catch(error => {
					return ErrorHandler.handleError(error, 'Failed to initialize canvas');
				});
			} catch (error) {
				return ErrorHandler.handleError(error, 'Initialization error');
			}
		},

		setupEventListeners() {
			const eventMap = {
				'text': this.updateText,
				'textColor': this.updateTextColor,
				'font': this.updateFont,
				'justification': this.updateJustification,
				'mirrored': this.updateMirrored,
				'italic': this.updateItalic,
				'bold': this.updateBold,
				'layerColors': this.updateLayerColors,
				'background': this.updateBackground,
				'debugMode': this.updateDebugMode
			};

			Object.entries(eventMap).forEach(([event, handler]) => {
				app.Store.subscribe(event, handler.bind(this));
			});

			app.Store.subscribe('flagPosition', (position) => {
				if (position && CanvasConfig.flag.isEnabled) {
					CanvasConfig.flag.position = position;
					CanvasState.debouncedDraw(true, true);
				}
			});

			// Subscribe to size changes to update the height measurement display
			app.Store.subscribe('size', (size) => {
				if (size && CanvasConfig.flag.isEnabled && CanvasState.builderType === 'flag') {
					// Redraw debug shapes to update the height pill
					DebugRenderer.drawDebugShapes();
				}
			});


			const debouncedResize = app.Utils.debounce(this.handleResize.bind(this), 100);
			window.addEventListener('resize', debouncedResize);
			
			this.hookHeightMeasurementSelect();
		},

		hookHeightMeasurementSelect() {
			const ATTACH = () => {
				let heightElement = document.querySelector('.wdpp-select--text-height')
				let customHeightElement = document.querySelector('.wdpp-textarea--custom-text-height');
				let supportElWidth = document.querySelector('.wdpp-textarea--custom-text-width');
				let supportElBase = document.querySelector('.wdpp-textarea--custom-text-baseheight');

				if(!heightElement){
					return false;
				}

				// Create a single handler that always gets fresh element references
				CanvasState._heightSelectHandler = () => {
					// Always redraw debug shapes when any height-related field changes
					DebugRenderer.drawDebugShapes();
				};

				// Attach to the select element
				heightElement.addEventListener('change', CanvasState._heightSelectHandler);
				//heightElement.addEventListener('input', CanvasState._heightSelectHandler);

				// Attach to custom fields if they exist
				if(customHeightElement) {
					customHeightElement.addEventListener('change', CanvasState._heightSelectHandler);
					customHeightElement.addEventListener('input', CanvasState._heightSelectHandler);
				}
				
				if(supportElWidth){
					supportElWidth.addEventListener('change', CanvasState._heightSelectHandler);
					supportElWidth.addEventListener('input', CanvasState._heightSelectHandler);
				}
				
				if(supportElBase){
					supportElBase.addEventListener('change', CanvasState._heightSelectHandler);
					supportElBase.addEventListener('input', CanvasState._heightSelectHandler);
				}

				// Also watch for when custom fields appear/disappear when switching to/from custom
				const customFieldObserver = new MutationObserver(() => {
					// Re-attach listeners to custom fields if they appear
					let newCustomHeight = document.querySelector('.wdpp-textarea--custom-text-height');
					let newCustomWidth = document.querySelector('.wdpp-textarea--custom-text-width');
					let newCustomBase = document.querySelector('.wdpp-textarea--custom-text-baseheight');
					
					// Remove old listeners if elements exist in state
					if(CanvasState._customHeightEl && CanvasState._customHeightEl !== newCustomHeight) {
						CanvasState._customHeightEl.removeEventListener('change', CanvasState._heightSelectHandler);
						CanvasState._customHeightEl.removeEventListener('input', CanvasState._heightSelectHandler);
					}
					
					// Add listeners to new elements
					if(newCustomHeight && newCustomHeight !== CanvasState._customHeightEl) {
						newCustomHeight.addEventListener('change', CanvasState._heightSelectHandler);
						newCustomHeight.addEventListener('input', CanvasState._heightSelectHandler);
						CanvasState._customHeightEl = newCustomHeight;
					}
					
					if(newCustomWidth && newCustomWidth !== CanvasState._customWidthEl) {
						if(CanvasState._customWidthEl) {
							CanvasState._customWidthEl.removeEventListener('change', CanvasState._heightSelectHandler);
							CanvasState._customWidthEl.removeEventListener('input', CanvasState._heightSelectHandler);
						}
						newCustomWidth.addEventListener('change', CanvasState._heightSelectHandler);
						newCustomWidth.addEventListener('input', CanvasState._heightSelectHandler);
						CanvasState._customWidthEl = newCustomWidth;
					}
					
					if(newCustomBase && newCustomBase !== CanvasState._customBaseEl) {
						if(CanvasState._customBaseEl) {
							CanvasState._customBaseEl.removeEventListener('change', CanvasState._heightSelectHandler);
							CanvasState._customBaseEl.removeEventListener('input', CanvasState._heightSelectHandler);
						}
						newCustomBase.addEventListener('change', CanvasState._heightSelectHandler);
						newCustomBase.addEventListener('input', CanvasState._heightSelectHandler);
						CanvasState._customBaseEl = newCustomBase;
					}
				});
				
				// Observe the container where custom fields appear/disappear
				const container = document.querySelector('.wdpp-container') || document.body;
				customFieldObserver.observe(container, { childList: true, subtree: true });
				CanvasState._customFieldObserver = customFieldObserver;

				// Initial paint to reflect current value
				DebugRenderer.drawDebugShapes();
				return true;
			};

			// Try to attach now
			if (ATTACH()) return;

			// If not present yet, observe the DOM until it appears
			const mo = new MutationObserver(() => {
				if (ATTACH()) {
					mo.disconnect();
				}
			});
			mo.observe(document.documentElement, { childList: true, subtree: true });
			CanvasState._heightSelectObserver = mo;
		},

		updateText(newText) {
			if (!CanvasState.textNode || typeof newText !== 'string') return;
			CanvasConfig.styles.text = newText;
			CanvasState.debouncedDraw(true, false);
		},

		updateTextColor(colorName) {
			if (!CanvasState.textNode || !CanvasState.colorMap[colorName]) return;
			CanvasConfig.styles.color = CanvasState.colorMap[colorName].hex;
			CanvasState.debouncedDraw(true, false);
		},

		updateFont(font) {
			if (!CanvasState.textNode || typeof font !== 'string') return;
			CanvasConfig.styles.font = font;
			CanvasState.debouncedDraw(true, false);
		},

		updateJustification(justification) {
			if (!CanvasState.textNode || !['left', 'center', 'right'].includes(justification)) return;
			CanvasConfig.styles.justification = justification;
			CanvasState.debouncedDraw(true, false);
		},

		updateMirrored(mirrored) {
			if (!CanvasState.textNode || typeof mirrored !== 'boolean') return;
			CanvasConfig.styles.mirrored = mirrored;
			CanvasState.debouncedDraw(true, true);
		},

		updateItalic(italic) {
			if (!CanvasState.textNode || typeof italic !== 'boolean') return;
			CanvasConfig.styles.italic = italic;
			CanvasState.debouncedDraw(true, false);
		},

		updateBold(bold) {
			if (!CanvasState.textNode || typeof bold !== 'boolean') return;
			CanvasConfig.styles.bold = bold;
			CanvasState.debouncedDraw(true, false);
		},

		updateLayerColors(layerColors) {
			if (!Array.isArray(layerColors) || !CanvasState.layerImages) return;
			
			const colorMap = new Map(layerColors.map(color => [color.id, color.name]));
			CanvasState.layerImages.forEach(layer => {
				if (colorMap.has(layer.id)) {
					layer.color = colorMap.get(layer.id);
				}
			});
			
			CanvasState.debouncedDraw(false, true);
		},

		updateBackground(config) {
			if (!config || typeof config !== 'object') return;

			if (typeof config.enabled === 'boolean') {
				CanvasConfig.checkerboard.enabled = config.enabled;
			}

			if (typeof config.tileSize === 'number' && config.tileSize > 0) {
				CanvasConfig.checkerboard.tileSize = config.tileSize;
			}

			if (config.colors && typeof config.colors === 'object') {
				CanvasConfig.checkerboard.colors = {
					...CanvasConfig.checkerboard.colors,
					...config.colors
				};
			}

			CanvasState.debouncedDraw(false, true);
		},

		updateDebugMode(enabled) {
			if (typeof enabled !== 'boolean') return;
			CanvasConfig.styles.debug = enabled;
			DebugRenderer.drawDebugShapes();
		},

		doDraw(text = true, layers = true) {
			if (!CanvasState.stage) {
				return ErrorHandler.handleError(ErrorHandler.errors.STAGE_NOT_INITIALIZED);
			}

			if (text) {
				// First redraw the text
				TextRenderer.redraw();
				
				// Then update measurements - this needs to be synchronous
				app.TextMeasurement.calculateByBaseHeight(true, true);
			}

			if (layers) {
				LayerManager.updateLayers();
			}

			PerformanceOptimizer.queueBatchDraw(CanvasState.layerText);
			DebugRenderer.drawDebugShapes();
		},

		createCheckerboardBackground() {
			if (!CanvasConfig.checkerboard.enabled || !CanvasState.layerBackground) {
				return ErrorHandler.handleError(ErrorHandler.errors.LAYER_NOT_READY);
			}

			const width = CanvasState.stage.width();
			const height = CanvasState.stage.height();
			const tileSize = CanvasConfig.checkerboard.tileSize;
			const colors = CanvasConfig.checkerboard.colors;

			const tilesX = Math.ceil(width / tileSize);
			const tilesY = Math.ceil(height / tileSize);

			CanvasState.layerBackground.destroyChildren();

			const group = new Konva.Group({ listening: false });
			
			// Base rectangle
			group.add(new Konva.Rect({
				x: 0,
				y: 0,
				width: width,
				height: height,
				fill: colors.light,
				listening: false
			}));

			// Create dark tiles
			const darkTiles = [];
			for (let y = 0; y < tilesY; y++) {
				for (let x = 0; x < tilesX; x++) {
					if ((x + y) % 2 !== 0) {
						darkTiles.push(new Konva.Rect({
							x: x * tileSize,
							y: y * tileSize,
							width: tileSize,
							height: tileSize,
							fill: colors.dark,
							perfectDrawEnabled: false,
							transformsEnabled: 'position',
							listening: false
						}));
					}
				}
			}
			group.add(...darkTiles);

			CanvasState.layerBackground.add(group);
			PerformanceOptimizer.queueBatchDraw(CanvasState.layerBackground);
		},

		defineCustomFilters() {
			Konva.Filters.Colorize = function(imageData) {
				const data = imageData.data;
				const color = this.colorToApply || { r: 0, g: 0, b: 0 };

				for (let i = 0; i < data.length; i += 4) {
					if (data[i + 3] > 0) {
						data[i] = color.r;
						data[i + 1] = color.g;
						data[i + 2] = color.b;
					}
				}
			};
		},

		getStyles() {
			return { ...CanvasConfig.styles };
		},

		getStyleString() {
			return CanvasUtils.getStyleString();
		},

		getFlagContext() {
			const baseFontSizeInRedraw = 100;
			return {
				isEnabled: CanvasConfig.flag.isEnabled && CanvasState.builderType === 'flag',
				image: CanvasConfig.flag.layer ? CanvasConfig.flag.layer.img : null,
				spacing: CanvasConfig.flag.spacing,
				baseFontSizeForSpacing: baseFontSizeInRedraw
			};
		},

		exportPreviewImage() {
			if (!CanvasState.stage) {
				return '';
			}

			try {
				const thumbnailSize = 176;
				const stageWidth = CanvasState.stage.width();
				const stageHeight = CanvasState.stage.height();
				const scale = Math.min(thumbnailSize / stageWidth, thumbnailSize / stageHeight);
				const exportWidth = Math.max(1, stageWidth);
				const exportHeight = Math.max(1, stageHeight);
				const canvas = document.createElement('canvas');
				const ctx = canvas.getContext('2d');

				if (!ctx) {
					return CanvasState.stage.toDataURL({
						pixelRatio: scale,
						mimeType: 'image/png'
					});
				}

				canvas.width = thumbnailSize;
				canvas.height = thumbnailSize;
				ctx.clearRect(0, 0, thumbnailSize, thumbnailSize);
				ctx.drawImage(
					CanvasState.stage.toCanvas(),
					(thumbnailSize - exportWidth * scale) / 2,
					(thumbnailSize - exportHeight * scale) / 2,
					exportWidth * scale,
					exportHeight * scale
				);

				return canvas.toDataURL('image/png');
			} catch (error) {
				console.error('Failed to export canvas preview image', error);
				return '';
			}
		},

		// Public API for error handling
		getError() {
			return CanvasState.error;
		},

		isReady() {
			return CanvasState.isInitialized && !CanvasState.error;
		},

		initializeCanvas() {
			const canvasEl = document.querySelector('.wdpp-canvas');
			if (!canvasEl) {
				return ErrorHandler.handleError(ErrorHandler.errors.CANVAS_NOT_FOUND);
			}

			CanvasState.colorMap = app.Store.getState('colorMap') || {};
			CanvasState.builderType = app.Store.getState('builderType') || '';

			if (CanvasState.builderType === 'flag') {
				CanvasConfig.flag.isEnabled = true;
				CanvasConfig.flag.position = app.Store.getState('flagPosition') || 'left';
				CanvasConfig.styles.justification = 'left';
			}

			this.defineCustomFilters();

			CanvasConfig.elements.canvasContainer = document.createElement('div');
			CanvasConfig.elements.canvasContainer.className = 'wdpp-canvas-container';
            const initialSize = CanvasUtils.getStageDimensions(canvasEl);

			Object.assign(CanvasConfig.elements.canvasContainer.style, {
                width: `${initialSize.width}px`,
				height: `${initialSize.height}px`,
				position: 'relative'
			});
			canvasEl.parentNode.replaceChild(CanvasConfig.elements.canvasContainer, canvasEl);

			CanvasState.textBoundingBox = app.Store.getState('textBoundingBox') || {};

            /*
			const width = CanvasConfig.elements.canvasContainer.clientWidth || CanvasConfig.defaults.width;
			const height = CanvasConfig.elements.canvasContainer.clientHeight || CanvasConfig.defaults.height;
            */
			const { width, height } = CanvasUtils.getStageDimensions(canvasEl);

			CanvasState.stage = new Konva.Stage({
				container: CanvasConfig.elements.canvasContainer,
				width,
				height
			});

			// Initialize layers
			[
				'layerBackground',
				'layerBase',
				'layerText',
				'layerDebug'
			].forEach(layerName => {
				CanvasState[layerName] = new Konva.Layer();
				CanvasState.stage.add(CanvasState[layerName]);
			});

			CanvasState.textFlagGroup = new Konva.Group();
			CanvasState.layerText.add(CanvasState.textFlagGroup);

			CanvasState.textNode = new Konva.Text({
				text: CanvasConfig.styles.text,
				fontFamily: CanvasConfig.styles.font,
				fontStyle: 'normal',
				fill: CanvasConfig.styles.color,
				align: CanvasConfig.styles.justification
			});

			/* advancedMeasurements */
			const canvasWrapper = document.querySelector('.wdpp-image-wrapper');
			if(canvasWrapper){
				if(canvasWrapper.classList.contains('wdpp-image-wrapper--advanced-measurements')){
					CanvasConfig.advancedMeasurements = true;
					console.log('advancedMeasurements');
				}
			}

			return true;
		},

		handleResize() {
			if (!CanvasState.isInitialized) return;

            /*
			const newWidth = CanvasConfig.elements.canvasContainer.clientWidth || CanvasConfig.defaults.width;
			const newHeight = CanvasConfig.elements.canvasContainer.clientHeight || CanvasConfig.defaults.height;
			*/

            const { width: newWidth, height: newHeight } = CanvasUtils.getStageDimensions();
			CanvasConfig.elements.canvasContainer.style.width = `${newWidth}px`;
			CanvasConfig.elements.canvasContainer.style.height = `${newHeight}px`;


			CanvasState.stage.width(newWidth);
			CanvasState.stage.height(newHeight);
			
			this.createCheckerboardBackground();
			LayerManager.updateLayers();
			CanvasState.debouncedDraw();
		},
	};
})(window.app = window.app || {});
